import PropTypes from 'prop-types';
import React from 'react';
import { injectIntl, FormattedPlural } from 'react-intl';
import cx from 'classnames';
import { Button, Flexer, Spacer } from '@ontruck/orc';
import IBANModule from 'iban';
import tel from 'utils/phone';
import config from 'config';
import moment from 'moment';
import CarrierTarget from './CarrierTarget';
import CarrierScoring from './CarrierScoring';

import style from './style.module.scss';
import useCarriersLite from 'utils/hooks/useCarriersLite';

const Detail = ({ children, className }) =>
  children ? <span className={cx(style.infoDetail, className)}>{children}</span> : null;

Detail.propTypes = {
  children: PropTypes.any,
  className: PropTypes.string,
};

const CarrierInfo = ({ intl, carrier, onEditCarrier, onFilterCarrier }) => {
  const t = intl.messages.components.CarrierDetails;
  const { isScoreDisabled, isOdooDisabled, isEditCarrierDisabled, isPODEmailDisabled } =
    useCarriersLite();

  return (
    <div className={style.section}>
      <div className={style.titleContainer}>
        <h2 className={style.sectionTitle}>{t.carrier_info_title}</h2>
        <div className={style.sectionTitleActions}>
          <div>
            <span className={style.completedOrdersButton}>
              <Button nude onClick={onFilterCarrier}>
                {carrier.number_of_journeys}{' '}
                <FormattedPlural
                  value={carrier.number_of_journeys}
                  one={t.journey_finished}
                  other={t.journeys_finished}
                />
              </Button>
            </span>
            {!isScoreDisabled && (
              <>
                <CarrierScoring carrier={carrier} />
                <span className={style.separator}>-</span>
                <CarrierTarget carrier={carrier} />
              </>
            )}
          </div>
          <div>
            {carrier.odoo_id && !isOdooDisabled && (
              <>
                <span className={style.odooButton}>
                  <Button
                    nude
                    target="_blank"
                    href={`${config.ODOO_URL}web?debug=#id=${carrier.odoo_id}&view_type=form&model=res.partner&menu_id=102&action=101`}
                  >
                    {t.carrier_info_odoo}
                  </Button>
                </span>
                <span className={style.separator}>-</span>
              </>
            )}
            {!isEditCarrierDisabled && (
              <span className={style.editButton}>
                <Button nude onClick={onEditCarrier}>
                  {t.carrier_info_edit}
                </Button>
              </span>
            )}
          </div>
        </div>
      </div>
      <div className={style.carrierInfo}>
        <div>
          <h3 className={style.infoTitle}>{t.carrier_info_address}</h3>
          {carrier.billing_address ? (
            <Detail>{carrier.billing_address.address_line}</Detail>
          ) : (
            <Detail className={style.warning}>{t.carrier_info_no_address}</Detail>
          )}
          {carrier.billing_address ? (
            <div>
              <Detail>{carrier.billing_address.zipcode}</Detail>
              <Detail>{carrier.billing_address.city}</Detail>
              <Detail>{carrier.fiscal_country}</Detail>
            </div>
          ) : null}
          <Detail>
            <b data-testid="company_creation_date_info">{t.company_creation_date}:</b>{' '}
            {carrier.company_creation_date
              ? moment(carrier.company_creation_date).format('DD/MM/YYYY')
              : null}
          </Detail>
        </div>

        <div>
          <h3 className={style.infoTitle}>{t.carrier_info_contact}</h3>
          <Flexer.Parent>
            <div>
              <Detail>{carrier.contact_name}</Detail>
              {carrier.phone && (
                <Detail>
                  <Button nude className={style.phone} href={tel(carrier.phone)}>
                    {carrier.phone}
                  </Button>
                </Detail>
              )}
              {carrier.phone_secondary && (
                <Detail>
                  <Button nude className={style.phone} href={tel(carrier.phone_secondary)}>
                    {carrier.phone_secondary}
                  </Button>
                </Detail>
              )}
            </div>
            <Spacer mLeft="L" mTop="XS">
              {carrier.email && (
                <Detail>
                  <b>{t.carrier_contact_email}:</b>
                  <Button nude className={style.email} href={`mailto:${carrier.email}`}>
                    {carrier.email}
                  </Button>
                </Detail>
              )}
              {carrier.pod_email && !isPODEmailDisabled && (
                <Detail>
                  <b>{t.carrier_pod_email}:</b>
                  <Button nude className={style.email} href={`mailto:${carrier.pod_email}`}>
                    {carrier.pod_email}
                  </Button>
                </Detail>
              )}
              <Detail>
                <b>{t.carrier_accepted_terms}:</b> {carrier.accepted_terms}
              </Detail>
            </Spacer>
          </Flexer.Parent>
        </div>

        <div>
          <h3 className={style.infoTitle}>{t.carrier_info_payment}</h3>
          {carrier.tax_number ? (
            <Detail>{carrier.tax_number}</Detail>
          ) : (
            <Detail className={style.warning}>{t.carrier_info_no_tax_number}</Detail>
          )}
          {carrier.iban && <Detail>{IBANModule.printFormat(carrier.iban, ' ')}</Detail>}
          {carrier.billing_email && (
            <Detail>
              <b>{t.carrier_billing_email}:</b>
              <Button nude className={style.email} href={`mailto:${carrier.billing_email}`}>
                {carrier.billing_email}
              </Button>
            </Detail>
          )}
        </div>
      </div>
    </div>
  );
};

CarrierInfo.propTypes = {
  carrier: PropTypes.object,
  onEditCarrier: PropTypes.func,
  intl: PropTypes.any,
  onFilterCarrier: PropTypes.func,
};

export default injectIntl(CarrierInfo);
